import type { ResolvedVisual, RichText } from '@matter_extractor/shared';
import { NOYAU_CHOICE_BUTTON_LABEL, renderRichText } from '@matter_extractor/shared';
import { Check } from 'lucide-react';
import { type ReactNode, useId, useMemo } from 'react';
import {
  type ExperienceNoyauBlock,
  READER_TEST_IDS,
  type ReaderMode,
  type ReaderNoyau,
  type ReaderNoyauCard,
} from '../contracts';

export interface NoyauBlockProps {
  readonly block: ExperienceNoyauBlock;
  readonly visual: ResolvedVisual;
  readonly mode: ReaderMode;
  /** What the Noyau blocks of the page show, or undefined for the empty frames. */
  readonly noyau: ReaderNoyau | undefined;
  /** The card selected - `values[block.id]` - or null. */
  readonly selected: string | null;
  /** An obligatory block that has ALREADY APPEARED is missing (§9.2, H30). */
  readonly blocked: boolean;
  /** The sentence that says why the confirmation is grey. Rendered under ONE exit per page. */
  readonly hint: string | null;
  readonly onSelect: (cardId: string) => void;
  readonly onConfirm: (cardId: string) => void;
}

/** The legend of each group of cards, read by assistive technology and never drawn (maquette `Main`). */
const LEGEND = {
  'first-choice': 'Choisissez le texte qui vous ressemble le plus',
  'second-choice': 'Choisissez entre ces deux textes',
} as const;

/** What an empty frame says: a surface that reads no text - the scene of the Timeline, a vignette. */
const EMPTY_CARD = 'Texte d’un noyau';
const EMPTY_PORTRAIT = 'Portrait du noyau retenu';

/**
 * The Noyau block (noyau.md §3.2, §5; maquettes `Main`, `ChoixEcran`): in a
 * choice mode, two SELECTABLE cards and one button; in portrait mode, the
 * portrait of the retained noyau.
 *
 * ## A radio group, and a button that confirms (§5.1, N29, N30)
 *
 * Each card is a `<label>` wrapping a visually hidden `<input type="radio">`,
 * the two inside a `<fieldset>` whose `<legend>` is hidden too - the group the
 * spec asks for, and what the maquette draws: a click, a touch or a key selects
 * a card, a click on the other moves the selection, the arrows move it within
 * the group, and the focus ring is drawn on the CARD (`:has(:focus-visible)` in
 * `reader.css`). The selection is a VALUE the caller holds (`values[block.id]`),
 * never state of the reader. « Choisir ce texte » - a fixed label (N29) - is
 * grey until a card is selected, and confirms THAT card; the selections before
 * it are nobody's business (N30), so they never leave the caller.
 *
 * ## A card shows its text, and nothing else (N19, N28)
 *
 * No name, no triplet, no colour, no « principal »: the reader receives two
 * identifiers and two texts, and draws exactly that. The text is the restricted
 * rich text of the Texte block, rendered by the SAME pure function
 * (`renderRichText`) - « il n'existe pas deux orthographes d'un texte riche » -
 * and WITHOUT a resolver: the texts of the noyaux carry no variable (§6.2), so
 * a `{{...}}` a specialist typed is read as the words it is.
 *
 * `dangerouslySetInnerHTML` here as in `text-block.tsx`, and for its reason:
 * `renderRichText` escapes every character of text it emits, so no keystroke of
 * anybody's becomes an element.
 *
 * ## Painted by the theme, like the rest of the page
 *
 * At rest the card is the surface of the interviewer's bubble, under a muted
 * rule; selected, a two-pixel rule in the accent, a tint mixed from the accent,
 * and a check in an accent disc - all of it `--lecteur-*`, so a theme that
 * changes its accent repaints the selection with it. Side by side on a screen,
 * one under the other on a telephone, the button always under both.
 */
export function NoyauBlock({
  block,
  visual,
  mode,
  noyau,
  selected,
  blocked,
  hint,
  onSelect,
  onConfirm,
}: NoyauBlockProps): ReactNode {
  const group = useId();

  if (block.mode === 'portrait') {
    const portrait = noyau?.portrait ?? null;
    return portrait === null ? (
      <p className="lecteur-noyau-vide" data-testid={READER_TEST_IDS.noyauPortrait}>
        {EMPTY_PORTRAIT}
      </p>
    ) : (
      <RichTextView text={portrait} testId={READER_TEST_IDS.noyauPortrait} />
    );
  }

  const cards = noyau?.choices[block.id] ?? null;
  const taken = cards?.find((card) => card.cardId === selected) ?? null;
  return (
    <>
      <fieldset className="lecteur-noyau" data-testid={READER_TEST_IDS.noyauCards}>
        <legend className="lecteur-cache">{LEGEND[block.mode]}</legend>
        <div className="lecteur-noyau-cartes">
          {cards === null
            ? [0, 1].map((rank) => (
                <p key={rank} className="lecteur-noyau-carte lecteur-noyau-vide">
                  {EMPTY_CARD}
                </p>
              ))
            : cards.map((card) => (
                <Card
                  key={card.cardId}
                  card={card}
                  group={group}
                  selected={card.cardId === selected}
                  onSelect={onSelect}
                />
              ))}
        </div>
      </fieldset>
      <div className="lecteur-noyau-pied">
        <button
          type="button"
          className="lecteur-bouton lecteur-noyau-choisir"
          data-style={visual.buttonStyle}
          data-testid={READER_TEST_IDS.noyauConfirm}
          disabled={taken === null || blocked}
          onClick={
            mode === 'interactive' && taken !== null ? () => onConfirm(taken.cardId) : undefined
          }
        >
          {NOYAU_CHOICE_BUTTON_LABEL}
        </button>
        {blocked && hint !== null ? (
          <p className="lecteur-aide" data-testid={READER_TEST_IDS.advanceHint}>
            {hint}
          </p>
        ) : null}
      </div>
    </>
  );
}

/** One selectable card: the label, its hidden radio, its check, its text. */
function Card({
  card,
  group,
  selected,
  onSelect,
}: {
  readonly card: ReaderNoyauCard;
  readonly group: string;
  readonly selected: boolean;
  readonly onSelect: (cardId: string) => void;
}): ReactNode {
  return (
    <label
      className="lecteur-noyau-carte"
      data-testid={READER_TEST_IDS.noyauCard}
      data-card-id={card.cardId}
      data-selected={selected ? 'true' : 'false'}
    >
      <input
        type="radio"
        className="lecteur-cache"
        name={group}
        value={card.cardId}
        checked={selected}
        onChange={() => onSelect(card.cardId)}
      />
      {selected ? (
        <span className="lecteur-noyau-coche" aria-hidden="true">
          <Check className="lecteur-noyau-coche-icone" strokeWidth={3} />
        </span>
      ) : null}
      <RichTextView text={card.text} testId={null} />
    </label>
  );
}

/**
 * A text of a noyau, through the renderer of the Texte block (see the head).
 *
 * The `{ __html }` is MEMOIZED on the markup, and a card depends on it: React
 * re-sets `innerHTML` whenever that object is a new one, so every render would
 * replace the paragraphs under the pointer - and the first gesture on the page
 * DOES render (`page-reader.tsx` arms the sounds on `pointerdown`). The press
 * then lands on a paragraph that is gone by its `mousedown`, the browser fires
 * no `click`, and the label never checks its radio: a card that a click cannot
 * select, which jsdom, clicking the radio itself, never sees.
 */
function RichTextView({
  text,
  testId,
}: {
  readonly text: RichText;
  readonly testId: string | null;
}): ReactNode {
  const html = renderRichText(text, {});
  const markup = useMemo(() => ({ __html: html }), [html]);
  return (
    <div
      className="lecteur-texte"
      {...(testId === null ? {} : { 'data-testid': testId })}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: escaped by construction - see the head.
      dangerouslySetInnerHTML={markup}
    />
  );
}
